/**
 * Context provider for story selection state.
 *
 * Wraps a story-listing view (PublicationView, StoryList, etc) so child
 * components (StoryListTable, SelectionToolbar, etc) can access/modify selection
 * without prop drilling.
 *
 * Replaces compose-app `publicationViewState.selectAllStories` + `selectedStories`.
 *
 * Usage:
 * ```tsx
 * // Parent (PublicationView, StoryList)
 * <StoriesSelectionProvider>
 *   <SelectionToolbar />
 *   <StoryListTable />
 * </StoriesSelectionProvider>
 *
 * // Children
 * const selection = useStoriesSelectionContext();
 * <Checkbox checked={selection.isSelected(storyId)} onChange={() => selection.toggle(storyId)} />
 * ```
 *
 * StoryList bulk-select can use this same provider when it is migrated.
 */

import React, { createContext, useContext } from 'react';
import { useStoriesSelection, UseStoriesSelection } from '../hooks/useStoriesSelection';

export const StoriesSelectionContext = createContext<UseStoriesSelection | null>(null);

export type StoriesSelectionProviderProps = {
  children: React.ReactNode;
};

/**
 * Provider for story selection state. Wrap around any component tree that needs
 * to share selection state (checkboxes, select-all, bulk operations).
 */
export function StoriesSelectionProvider({ children }: StoriesSelectionProviderProps) {
  const selection = useStoriesSelection();

  return (
    <StoriesSelectionContext.Provider value={selection}>
      {children}
    </StoriesSelectionContext.Provider>
  );
}

/**
 * Hook to access story selection state from context.
 *
 * Must be used inside a <StoriesSelectionProvider>.
 *
 * @throws {Error} if used outside provider
 */
export function useStoriesSelectionContext(): UseStoriesSelection {
  const context = useContext(StoriesSelectionContext);

  if (!context) {
    throw new Error(
      'useStoriesSelectionContext must be used within a StoriesSelectionProvider. ' +
        'Wrap your component tree with <StoriesSelectionProvider>.'
    );
  }

  return context;
}
